import { useObjectView } from "@/hooks/use-object-view";
import { useCached3DObject, type PanoObject } from "@/object-cache";
import {
  getCameraAnimationTime,
  type CameraAnimationTimeOptions,
} from "@/utils/camera-animation-time";
import { usePanoCameraTransform } from "@/utils/camera-transform";
import {
  DollhouseAnimation,
  UniformLights,
  useNonExhaustiveEffect,
} from "@faro-lotv/app-component-toolbox";
import { Easing } from "@faro-lotv/foundation";
import type {
  IElementGenericImgSheet,
  IElementImg360,
} from "@faro-lotv/ielement-types";
import { useThree } from "@react-three/fiber";
import { useEffect, useReducer, useState, type JSX } from "react";
import { type Quaternion } from "three";
import { PanoramaRenderer } from "../renderers/panorama-renderer";
import { CameraAnimation } from "./camera-animation";

export type PanoPanoAnimationProps = {
  /** The panorama to render */
  pano: PanoObject;

  /** if provided quaternion should be applied while performing pano animation */
  targetQuaternion?: Quaternion;

  /** The zoom factor of the camera at the end of the animation */
  targetZoomLevel?: number;

  /** the sheet used to compute pano eye position for panos without a valid height */
  sheetForElevation?: IElementGenericImgSheet;

  /** The panorama to transition to */
  targetElement: IElementImg360;

  /**
   * Define to true to register this as a secondary view for split screen rendering
   *
   * @default false
   */
  isSecondaryView?: boolean;

  /** Callback to signal the animation was completed */
  onCompleted(targetElement: PanoObject): void;
};

/** Timings to use for 2d pano pano animation without a dollhouse */
const ANIMATION_TIME_2D: CameraAnimationTimeOptions = {
  min: 0.5,
  max: 1,
  scale: 10,
};

/**
 * @returns A scene to animate between two panorama images in PanoramaMode
 */
export function PanoPanoAnimation({
  pano,
  targetQuaternion,
  targetZoomLevel,
  sheetForElevation,
  targetElement,
  isSecondaryView = false,
  onCompleted,
}: PanoPanoAnimationProps): JSX.Element {
  const camera = useThree((s) => s.camera);
  const targetPosition = usePanoCameraTransform(
    targetElement,
    sheetForElevation,
  );

  const target = useCached3DObject(targetElement, "PanoPanoAnimation");

  // Prepare views for source and target panos so we don't steal them from a different
  // view if they're in use
  const sourceView = useObjectView(pano);
  const targetView = useObjectView(target);

  // Use the original pano as the source for the animation only if this is the main view
  // this remove one black flash at the end of the animation to re-adjust the original pano
  // opacity
  const sourceToUse = isSecondaryView ? sourceView : pano;

  // Duration should not be revaluated, because it could change
  // when the camera position changes
  const [duration] = useState(() =>
    getCameraAnimationTime(camera, targetPosition.position, ANIMATION_TIME_2D),
  );

  // In this case, useReducer is preferred over useState because returns a function
  // that can be used directly in the callbacks of the animations,
  // without the need to call useCallback and wrap them.
  const [isPanoAnimationFinished, onPanoAnimationFinished] = useReducer(
    () => true,
    false,
  );
  const [isCameraAnimationFinished, onCameraAnimationFinished] = useReducer(
    () => true,
    false,
  );

  // use non-exhaustive effect to make sure the callback only runs once
  useNonExhaustiveEffect(() => {
    // Once both animations finished, call the onCompleted callback
    if (isPanoAnimationFinished && isCameraAnimationFinished) {
      onCompleted(target);
    }
  }, [isCameraAnimationFinished, isPanoAnimationFinished]);

  const [showAnimation, setShowAnimation] = useState(false);

  return (
    <>
      {/*
      Since R3F build the scene only once all objects are properly mounted and processed, we need to add
      the panorama images to the scene first. Once they are ready, we add the DollhouseAnimation: this way
      we are sure that the world position of the elements is updated before starting the animation
    */}
      <PreRenderPanoramas
        source={sourceToUse}
        target={targetView}
        sheetForElevation={sheetForElevation}
        panosReady={() => setShowAnimation(true)}
      />
      {showAnimation && (
        <DollhouseAnimation
          from={sourceToUse}
          to={targetView}
          dollhouse={undefined}
          camera={camera}
          duration={duration}
          easing={Easing.linear}
          onCompleted={onPanoAnimationFinished}
        />
      )}
      <UniformLights />
      {showAnimation && (
        <CameraAnimation
          position={targetPosition.position}
          quaternion={targetQuaternion ?? camera.quaternion}
          fov={targetZoomLevel}
          duration={duration}
          onAnimationFinished={onCameraAnimationFinished}
        />
      )}
    </>
  );
}

type PanosProps = {
  /** Callback emitted once the component is mounted */
  panosReady(): void;

  /** The source pano of the animation */
  source: PanoObject;

  /** The target pano of the animation */
  target: PanoObject;

  /** The sheet used for the elevation of the pano */
  sheetForElevation: IElementGenericImgSheet | undefined;
};

/**
 * @returns An hidden group containing the source and target panorama
 * images of the animation
 */
function PreRenderPanoramas({
  panosReady,
  source,
  target,
  sheetForElevation,
}: PanosProps): JSX.Element {
  useEffect(() => panosReady(), [panosReady]);

  return (
    <group visible={false}>
      <PanoramaRenderer pano={source} sheetForElevation={sheetForElevation} />
      <PanoramaRenderer pano={target} sheetForElevation={sheetForElevation} />
    </group>
  );
}
